import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty, isNil, noop } from 'lodash';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
// Import UI Components
import { Checkbox, Icon } from '@geneui/components';
// Import Components
import BlockedBySubscriptionInfo from 'components/common/BlockedBySubscriptionInfo';
import FilterDependencyDots, { DependencyPill, FilterInfoPopover } from 'components/common/FilterDependencyDots';
// Import Constants
import { BLOCKED_BY_SUBSCRIPTION_CLASSNAME } from 'constants/common';
import { DraggableTypes } from './service';
// Import Styles
import styles from './Row.module.scss';

const Row = (props) => {
    const {
        isChecked,
        isPermanent,
        isDraggable,
        isDisabled,
        withSelect,
        withDisabled,
        hasInfoTooltip,
        name,
        description,
        schemaDeps,
        popoverPosition,
        onClick,
        dropRow,
        getDraggingRow,
    } = props;
    const [_isChecked, setIsChecked] = useState(!isPermanent && isChecked);
    const itemRef = useRef<Nullable<HTMLDivElement>>(null);
    const [itemClientWidth, setItemClientWidth] = useState(itemRef?.current?.clientWidth);

    const isFullyDisabled = withDisabled && isDisabled;
    const isPartiallyDisabled = !withDisabled && isDisabled;
    const hasDependencies = schemaDeps.length > 0;
    const hasDescription = hasInfoTooltip && !isEmpty(description);
    const infoTitle = typeof name === 'string' || typeof name === 'number' ? String(name) : '';
    const blockedClassName = classNames({ [BLOCKED_BY_SUBSCRIPTION_CLASSNAME]: isPartiallyDisabled });

    useEffect(() => {
        if (!isPermanent) {
            setIsChecked(() => isChecked);
        } else {
            onClick(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChecked]);

    const handleClick = (e) => {
        const { checked } = e.currentTarget;
        setIsChecked(checked);
        onClick(checked);
    };

    // eslint-disable-next-line no-unused-vars
    const [__, drop] = useDrop({
        accept: DraggableTypes.Row,
        drop: (item, _monitor) => {
            dropRow(item, getDraggingRow());
        },
        collect: (monitor) => {
            return {
                isOver: !!monitor.isOver(),
            };
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: DraggableTypes.Row,
        item: {
            ...getDraggingRow(),
            type: DraggableTypes.Row,
            style: { width: itemClientWidth },
            withSelect: withSelect,
            hasInfoTooltip: hasInfoTooltip,
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    });

    preview(getEmptyImage(), { captureDraggingState: true });

    const attachRef = (current) => {
        if (isPartiallyDisabled) return;
        itemRef.current = current;
        setItemClientWidth(itemRef.current?.clientWidth);
        if (isDraggable) {
            drag(current);
            drop(current);
        }
    };

    return (
        <BlockedBySubscriptionInfo isBlocked={isFullyDisabled || isPartiallyDisabled}>
            <div
                ref={attachRef}
                className={classNames('d-a-d-item', {
                    active: true,
                    selected: _isChecked,
                    'selected-item': isPermanent,
                    'is-draggable': isPartiallyDisabled ? false : isDraggable,
                    [BLOCKED_BY_SUBSCRIPTION_CLASSNAME]: isFullyDisabled,
                    disabled: isPartiallyDisabled,
                })}
            >
                <div className={classNames('d-a-d-item-label-part', styles.rowLabelPart)}>
                    {withSelect && (
                        <Checkbox
                            checked={_isChecked ?? false}
                            indeterminate={isNil(_isChecked)}
                            onChange={isFullyDisabled ? undefined : handleClick}
                            disabled={isFullyDisabled}
                        />
                    )}
                    <span className={blockedClassName}>{name}</span>
                </div>

                {(hasDependencies || hasDescription || isDraggable) && (
                    <div className={styles.rowActions}>
                        {(hasDependencies || hasDescription) && (
                            <DependencyPill className={blockedClassName}>
                                {hasDependencies && (
                                    <FilterDependencyDots
                                        schemaDeps={schemaDeps}
                                        isDragging={isDragging}
                                        popoverPosition={popoverPosition}
                                    />
                                )}
                                {hasDescription && (
                                    <FilterInfoPopover
                                        title={infoTitle}
                                        description={description}
                                        isDragging={isDragging}
                                        popoverPosition={popoverPosition}
                                    />
                                )}
                            </DependencyPill>
                        )}
                        {isDraggable && <Icon type="bc-icon-drag" className={blockedClassName} />}
                    </div>
                )}
            </div>
        </BlockedBySubscriptionInfo>
    );
};

Row.propTypes = {
    isChecked: PropTypes.bool,
    isPermanent: PropTypes.bool,
    isDraggable: PropTypes.bool,
    isDisabled: PropTypes.bool,
    withSelect: PropTypes.bool,
    withDisabled: PropTypes.bool,
    hasInfoTooltip: PropTypes.bool,
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]).isRequired,
    onClick: PropTypes.func,
    dropRow: PropTypes.func,
    getDraggingRow: PropTypes.func,
    description: PropTypes.string,
    schemaDeps: PropTypes.array,
    popoverPosition: PropTypes.oneOf(['left', 'right']),
};

Row.defaultProps = {
    isChecked: false,
    isPermanent: false,
    isDraggable: false,
    isDisabled: false,
    withSelect: true,
    withDisabled: false,
    name: '',
    onClick: noop,
    dropRow: noop,
    getDraggingRow: noop,
    schemaDeps: [],
    popoverPosition: 'right',
};

export default Row;
